import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { X, Check } from 'lucide-react';

import AsyncGenericSelect from '../../../components/ui/AsyncGenericSelect';
import TextInput from '../../../components/ui/TextInput.jsx';
import CheckboxInput from '../../../components/ui/CheckboxInput';
import Button from '../../../components/ui/Button';
import { RECOGNITION_BADGES, buildSelfExcludedEmployeeAsync } from '../config';

// Faz D (spec 2026-08-08 §4, Task 5, D5) — "Takdir Gönder" modalı.
// one-on-ones/components/NewMeetingModal.js ile AYNI Overlay/Modal/Header/
// Footer iskeleti (desen aynası). Rozet seçimi GenericSelect DEĞİL — brief'in
// istediği "6 rozet görsel seçim ızgarası": her rozet kendi gradyanlı ikon
// kutusuyla tıklanabilir bir kart (match-design-language memory: menü/rozet
// ikonları GRADYAN, my-badges/MyBadgesPage.js'teki dairesel ikon deseninin
// AYNISI). "Duvarda görünsün" varsayılan AÇIK — CreateRecognitionRequest.IsPublic
// varsayılanı true (task-3-report.md §1: "İSTEMCİ ALANI HİÇ GÖNDERMEZSE
// varsayılan true kalır") — burada AÇIKÇA true gönderilir (istemci niyetini
// gizli bir BE varsayımına bırakmamak için, toggle kapatılırsa false gönderilir).
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 42, 42, 0.4);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 6px;
  width: 560px;
  max-width: 92vw;
  max-height: 88vh;
  overflow-y: auto;
  padding: 28px;
  box-shadow: 0 16px 48px rgba(0, 66, 66, 0.15);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const Title = styled.h3`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const CloseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;
  color: #A0A8B4;
  &:hover:not(:disabled) { background: #F1F5F9; color: #004242; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const Field = styled.div`
  margin-bottom: 18px;
`;

const FieldLabel = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin-bottom: 8px;
`;

const BadgeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  @media (max-width: 520px) { grid-template-columns: repeat(2, 1fr); }
`;

const BadgeTile = styled.button`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 14px 8px;
  border-radius: 8px;
  border: 2px solid ${({ $selected, theme }) => ($selected ? theme.colors.primary.main : '#E8ECF0')};
  background: ${({ $selected }) => ($selected ? '#EFF6FF' : '#fff')};
  cursor: pointer;
  transition: all 0.15s;

  &:hover { border-color: ${({ theme }) => theme.colors.primary.main}; }
`;

const BadgeIconTile = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: ${({ $from, $to }) => `linear-gradient(135deg, ${$from}, ${$to})`};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BadgeTileLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: center;
  line-height: 1.3;
`;

const SelectedMark = styled.div`
  position: absolute;
  top: 6px;
  right: 6px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary.main};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 8px;
`;

export default function SendRecognitionModal({ excludeEmployeeId, onClose, onCreate }) {
  const { t } = useTranslation();

  const [receiverEmployeeId, setReceiverEmployeeId] = useState(null);
  const [badgeKey, setBadgeKey] = useState(null);
  const [text, setText] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [saving, setSaving] = useState(false);

  const receiverAsync = useMemo(
    () => buildSelfExcludedEmployeeAsync(excludeEmployeeId),
    [excludeEmployeeId],
  );

  const valid = !!receiverEmployeeId && !!badgeKey;

  const handleSubmit = useCallback(async () => {
    setSaving(true);
    const created = await onCreate({
      receiverEmployeeId,
      badgeKey,
      text: text.trim() ? text.trim() : undefined,
      isPublic,
    });
    if (created) {
      onClose();
      return;
    }
    setSaving(false);
  }, [onCreate, onClose, receiverEmployeeId, badgeKey, text, isPublic]);

  return (
    <Overlay onClick={saving ? undefined : onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>{t('recognitions.modal.title')}</Title>
          <CloseBtn onClick={onClose} disabled={saving}><X size={18} /></CloseBtn>
        </Header>

        <Field>
          <AsyncGenericSelect
            label={t('recognitions.modal.employeeLabel')}
            value={receiverEmployeeId}
            onChange={setReceiverEmployeeId}
            onSearch={receiverAsync.onSearch}
            onGetById={receiverAsync.onGetById}
            required
          />
        </Field>

        <Field>
          <FieldLabel>{t('recognitions.modal.badgeLabel')}</FieldLabel>
          <BadgeGrid>
            {RECOGNITION_BADGES.map((b) => (
              <BadgeTile
                key={b.key}
                type="button"
                data-role={`badge-tile-${b.key}`}
                $selected={badgeKey === b.key}
                onClick={() => setBadgeKey(b.key)}
              >
                {badgeKey === b.key && <SelectedMark><Check size={11} /></SelectedMark>}
                <BadgeIconTile $from={b.from} $to={b.to}>
                  <b.Icon size={20} />
                </BadgeIconTile>
                <BadgeTileLabel>
                  {t(`recognitions.badge.${b.key}`, { defaultValue: b.key })}
                </BadgeTileLabel>
              </BadgeTile>
            ))}
          </BadgeGrid>
        </Field>

        <Field>
          <TextInput
            label={t('recognitions.modal.textLabel')}
            type="textarea"
            rows={3}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </Field>

        <Field>
          <CheckboxInput
            label={t('recognitions.modal.publicToggle')}
            checked={isPublic}
            onChange={setIsPublic}
          />
        </Field>

        <Footer>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button data-role="create-recognition-btn" onClick={handleSubmit} disabled={!valid || saving}>
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        </Footer>
      </Modal>
    </Overlay>
  );
}
